import { Link, type LinkProps } from "@tanstack/react-router";
import clsx from "clsx";
import type { ReactNode } from "react";
import type { HelpTopic } from "../help/index.js";
import { HelpLink } from "./help.js";

/**
 * Top of every page: title, a short explanation of what the page is for and
 * what to do next, a link to the full help article, and the primary actions.
 */
export function PageHeader({
  title,
  intro,
  help,
  meta,
  actions,
}: {
  title: ReactNode;
  intro?: ReactNode;
  help?: HelpTopic;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    // A size container: the actions move beside the title by the page's width, not the
    // window's, since the right block may narrow the page (docs/web.md, "Layout").
    <header className="@container">
      <div className="flex flex-col gap-3 @2xl:flex-row @2xl:items-start @2xl:justify-between">
        <div className="flex min-w-0 max-w-3xl flex-col gap-1.5">
          {/* A long name is cut to one line; the tooltip has all of it. */}
          <h1
            className="truncate text-2xl font-semibold tracking-tight"
            title={typeof title === "string" ? title : undefined}
          >
            {title}
          </h1>
          {meta}
          {intro && <p className="text-sm leading-relaxed text-muted">{intro}</p>}
          {help && (
            <div>
              <HelpLink topic={help} />
            </div>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export interface TabItem {
  key: string;
  label: string;
  link: LinkProps;
}

/** Tabs as links, so each tab has its own URL and survives a reload. */
export function Tabs({
  items,
  active,
  label,
}: {
  items: TabItem[];
  active: string;
  label: string;
}) {
  return (
    <nav className="relative -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" aria-label={label}>
      <div className="flex min-w-max gap-1 border-b border-line">
        {items.map((item) => (
          <Link
            key={item.key}
            {...item.link}
            aria-current={item.key === active ? "page" : undefined}
            className={clsx(
              "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm",
              item.key === active
                ? "border-accent font-medium text-text"
                : "border-transparent text-muted hover:text-text",
            )}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

/** What an empty list shows: what the resource is, why you would want one, how to start. */
export function EmptyState({
  icon,
  title,
  children,
  action,
  help,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  action?: ReactNode;
  help?: HelpTopic;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-accent-soft text-accent">
        {icon}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="max-w-md text-sm leading-relaxed text-muted">{children}</p>
      {(action || help) && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-4">
          {action}
          {help && <HelpLink topic={help} />}
        </div>
      )}
    </div>
  );
}

/** Titled block inside a page, with an optional one-line explanation. */
export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-base font-semibold">{title}</h2>
          {description && <p className="text-sm text-muted">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}
